import { get } from '../services/request';

type BankList = {
  code: string;
  text: string;
  id: string;
};

export type BankOptions = {
  key: string;
  value: string;
  text: string;
  searchText: string;
};

export const normalizeBankCode = (code: string | number | undefined | null): string => {
  const digits = String(code || '').replace(/\D/g, '');
  if (!digits) return '';
  return `000${digits}`.slice(-3);
};

export const formatBankOptionText = (code: string, text: string): string => {
  let bankName = String(text || '').trim();
  if (!bankName) return code;

  const leadingCodePattern = new RegExp(`^${code}\\s*[-–]\\s*`, 'i');
  while (leadingCodePattern.test(bankName)) {
    bankName = bankName.replace(leadingCodePattern, '').trim();
  }

  return `${code} - ${bankName}`;
};

export const getBankNameFromOption = (option: BankOptions): string => {
  const prefix = `${option.value} - `;
  if (option.text.startsWith(prefix)) {
    return option.text.slice(prefix.length);
  }
  return option.text;
};

export const filterBankOptions = (options: BankOptions[], query: string): BankOptions[] => {
  const search = String(query || '').trim();
  if (!search) return options;

  const digits = search.replace(/\D/g, '');
  const isDigitSearch = digits.length > 0 && digits.length === search.length;

  if (isDigitSearch) {
    if (digits.length === 3) {
      const normalizedQuery = normalizeBankCode(digits);
      return options.filter(option => option.value === normalizedQuery);
    }

    return options.filter(option => {
      const code = option.value;
      const trimmedCode = code.replace(/^0+/, '') || '0';
      return code.startsWith(digits) || trimmedCode.startsWith(digits);
    });
  }

  return options.filter(option => option.searchText.includes(search.toLowerCase()));
};

const buildBankOption = (bank: BankList): BankOptions => {
  const code = normalizeBankCode(bank.code);
  const text = formatBankOptionText(code, bank.text);

  return {
    key: code,
    value: code,
    text,
    searchText: `${code} ${text}`.toLowerCase()
  };
};

let cachedBanks: BankOptions[] | null = null;
let cachePromise: Promise<BankOptions[]> | null = null;

export const listEstateBanks = async (): Promise<BankOptions[]> => {
  if (cachedBanks) return cachedBanks;

  if (!cachePromise) {
    cachePromise = get('/banks')
      .then((banks: BankList[]) => {
        cachedBanks = banks.map(buildBankOption);
        return cachedBanks;
      })
      .catch(error => {
        cachePromise = null;
        throw error;
      });
  }

  return cachePromise as Promise<BankOptions[]>;
};

export const getEstateBankByCode = async (code: string): Promise<BankOptions> => {
  const bank: BankList = await get(`/banks/${code}`);
  return buildBankOption(bank);
};
